<template>
  <div>
    <h1 class="page-header">Orders to pack <small>all listings.</small></h1>
    <b-card>
      <b-tabs content-class="mb-3">
        <b-tab title="Ready to pack" :active="$route.name === 'orders.to-pack'" @click="tabGoTo({ name: 'orders.to-pack' })">
        </b-tab>
        <b-tab title="Packed" :active="$route.name === 'orders.packed'" @click="tabGoTo({ name: 'orders.packed' })">
        </b-tab>
      </b-tabs>

      <b-table
        :fields="fields"
        :items="mapItems(items)"
        show-empty
        stacked="sm"
        bordered
        hover
        @row-clicked="row => showDetails(row)"
      >
        <template v-slot:cell(name)="{ item }">
          {{ `${item.first_name} ${item.last_name}` }}
        </template>

        <template v-slot:cell(paid)="{ item }">
          <b-badge :variant="item.paid ? 'primary' : 'warning'">
            {{ item.paid ? 'Paid' : 'Not Paid' }}
          </b-badge>
        </template>

        <template v-slot:cell(full_sum)="{ item }">
          {{ formatMoney(item.full_sum) }}
        </template>

        <template v-slot:cell(card_type)="{ item }">
          {{ formatCardTypes(item) }}
        </template>

        <template v-slot:cell(wrapper)="{ item }">
          {{ formatWrapperName(item.wrapper) }}
          <img :src="getWrapperImage(item.wrapper)" alt="" width="128">
        </template>

        <template v-slot:cell(options)="{ item }">
          <b-button variant="default" size="sm" class="mr-2" @click="markPacked([item])">
            Mark as packed
          </b-button>
          <b-button variant="default" size="sm" :href="printLocation([item])" target="_blank">
            Print card/address
          </b-button>
        </template>
        <template v-slot:row-details="{ item }">
          <b-overlay :show="loading === item.id">
            <order-details :order="item" @loaded="loading = 0" />
          </b-overlay>
        </template>
      </b-table>
      <hr v-if="items.length" />

      <b-row v-if="items.length" align-h="end">
        <b-col cols="auto">
          <b-button variant="outline-primary" class="mr-2" :href="printLocation(items)" target="_blank">
            <i class="fa fa-print" />
            Print vouchers
          </b-button>
          <b-button variant="primary" @click="markPacked(items)"> <i class="fa fa-check" /> Mark all as sent </b-button>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { orders } from '@/api'
import { formatDate, formatMoney } from '../page-helpers'
import OrderDetails from './OrderDetails.vue'

export default {
  name: 'OrdersToPack',
  components: { OrderDetails },
  data() {
    return {
      loading: 0,
      current: 0,
      items: [],
      ordersIndex: process.env.VUE_APP_ALGOLIA_ORDERS_INDEX,
    }
  },
  computed: {
    fields() {
      return [
        { key: 'id', label: 'ID', sortable: false },
        { key: 'name', sortable: false },
        { key: 'paid', sortable: false },
        { key: 'full_sum', sortable: false },
        { key: 'delivery_method', sortable: false },
        { key: 'card_type', sortable: false },
        { key: 'wrapper', sortable: false },
        { key: 'created_at', sortable: false, formatter: formatDate },
        { key: 'options' },
      ]
    },
  },
  mounted() {
    this.loadContent()
  },
  methods: {
    formatMoney,
    formatCardTypes(item) {
      const uniqueCodes = new Set()
      const orderDetails = item.order_details
        .filter(detail => detail.giftcard && detail.type !== 'order_addons')
        .filter(detail => {
          if (uniqueCodes.has(detail.code)) {
            return false
          }

          uniqueCodes.add(detail.code)
          return true
        })

      const classicCardsNumber = orderDetails.filter(detail => detail.giftcard.customized_greeting?.deliveryMethod === 'giftbox' && detail.giftcard.customized_greeting?.deliveryMethodDetails === 'Text giftcard').length
      const qrCodeNumber = orderDetails.length - classicCardsNumber

      if (qrCodeNumber > 0 && classicCardsNumber > 0) {
        return `${qrCodeNumber}x QR card; ${classicCardsNumber}x Classic card`
      }

      if (qrCodeNumber > 0) {
        return `${qrCodeNumber}x QR card`
      }

      return `${classicCardsNumber}x Classic card`
    },
    formatWrapperName(wrapper) {
      switch (wrapper) {
        case 'classic':
          return 'Klassisk'
        case 'minimalism':
          return 'BlackC: Simplistic'
        case 'elegance':
          return 'BlackC: Elegant'
        case 'exclusive':
          return 'BlackC: Exclusive'
        case 'legend':
          return 'ClassicS: To a legend'
        case 'santa':
          return 'ClassicS: Santa'
        case 'love-you':
          return 'ClassicS: Love you'
        case 'happy-birthday':
          return 'ClassicS: Happy birthday'
        case 'northern-light':
          return 'RibbonF: Northern Light'
        case 'pine':
          return 'RibbonF: Pine'
        case 'royal-blue':
          return 'RibbonF: Royal Blue'
        case 'soft-pink':
          return 'RibbonS: Soft Pink'
        case 'soft-mint':
          return 'RibbonS: Soft Mint'
        default:
          return wrapper
      }
    },
    async loadContent() {
      this.items = (await orders.all({ filter: { to_pack: true } })).data()
    },
    printLocation(items) {
      const url = this.$store.getters['market/enabled']
        ? this.$store.getters['market/apiWebUrl']
        : process.env.VUE_APP_API_URL
      const ids = items.map(item => item.id).join(',')
      return `${url}/orders/print?orders=${ids}`
    },
    showDetails(order) {
      this.loading = order.id
      this.current = order.id === this.current ? 0 : order.id
      order._showDetails = !order._showDetails
    },
    mapItems(items) {
      return items.map(item => ({ ...item, _showDetails: this.current === item.id }))
    },
    async markPacked(items) {
      await this.$store.dispatch('auth/refreshToken');
      await orders.markAsPacked(items.map(item => item.id))
      this.$bvToast.toast(`${items.length} order(s) marked as packed.`, {
        title: 'Orders sent.',
        variant: 'primary',
      })
      this.loadContent()
    },
    getWrapperImage(wrapperName) {
      return `${process.env.VUE_APP_BASE_URL}/img/wrappers/${wrapperName}.png`
    },

    tabGoTo(route) {
      if (route.name !== this.$route.name) {
        this.$router.push(route)
      }
    },
  },
}
</script>
